/**
 * The media service's public share API, called from the browser (spec #330, shapes per #334).
 * The token is the credential; a password link returns `share_session` once, and every later
 * call carries it as the `share_session` query parameter. No portal proxy, no portal secret.
 * @module web/review/api
 */
import { MEDIA_API, normalizeComments, normalizeLink, resolveStreamUrl, type LinkInfo, type ShareComment } from "./logic";

export class ShareError extends Error {
  constructor(readonly status: number, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.name = "ShareError";
  }
}

export interface ShareAsset {
  id: string;
  name: string;
  type: string;
  versionCount: number;
  thumbnailUrl: string | null;
}

export interface ShareSubfolder {
  id: string;
  name: string;
  itemCount: number;
  thumbnailUrls: string[];
}

export interface ShareListing {
  assets: ShareAsset[];
  subfolders: ShareSubfolder[];
}

export interface ShareVersion {
  id: string;
  number: number;
  ready: boolean;
  createdAt: string | null;
}

export interface NewComment {
  body: string;
  timecode: number | null;
  parentId: string | null;
  /** `{drawing_data, frame_number}` or null. */
  annotation: { drawing_data: unknown; frame_number: number } | null;
  idempotencyKey: string;
  versionId: string | null;
}

export interface ShareApi {
  /** First call of the page: `log_open=true`. With a password, returns the same object carrying `share_session`. */
  link(password?: string): Promise<LinkInfo>;
  listing(folderId?: string | null): Promise<ShareListing>;
  versions(assetId: string): Promise<ShareVersion[]>;
  stream(assetId: string, opts?: { download?: boolean; versionId?: string | null }): Promise<string>;
  comments(assetId: string, versionId?: string | null): Promise<ShareComment[]>;
  postComment(assetId: string, c: NewComment): Promise<ShareComment | null>;
}

const s = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
const PER_PAGE = 100;
const MAX_PAGES = 20;

export function makeShareApi(token: string, fetchImpl: typeof fetch = (...a) => fetch(...a), base: string = MEDIA_API): ShareApi {
  const root = `${base}/share/${encodeURIComponent(token)}`;
  let session: string | null = null;
  let opened = false;

  const call = async <T>(path: string, query: Record<string, string | null | undefined> = {}, init: RequestInit = {}): Promise<T> => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) if (v) q.set(k, v);
    if (session) q.set("share_session", session);
    const headers = new Headers(init.headers);
    if (init.body) headers.set("Content-Type", "application/json");
    const res = await fetchImpl(`${root}${path}${q.size ? `?${q}` : ""}`, { ...init, headers });
    if (!res.ok) throw new ShareError(res.status);
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  };

  return {
    async link(password) {
      const logOpen = opened ? null : "true";
      opened = true;
      const info = normalizeLink(await call<Record<string, unknown>>("", { password: password ?? null, log_open: logOpen }));
      if (password !== undefined) {
        if (!info.shareSession) throw new ShareError(401, "no session returned");
        session = info.shareSession;
      }
      return info;
    },
    async listing(folderId) {
      const assets: ShareAsset[] = [];
      let subfolders: ShareSubfolder[] = [];
      for (let page = 1; page <= MAX_PAGES; page++) {
        const r = await call<Record<string, unknown>>("/assets", { folder_id: folderId, page: String(page), per_page: String(PER_PAGE) });
        for (const a of Array.isArray(r?.assets) ? (r.assets as Array<Record<string, unknown>>) : []) {
          const id = s(a.id);
          if (!id) continue;
          assets.push({ id, name: s(a.name) ?? "Untitled", type: s(a.asset_type) ?? "video", versionCount: Number(a.version_count ?? 1) || 1, thumbnailUrl: s(a.thumbnail_url) });
        }
        if (page === 1) {
          subfolders = (Array.isArray(r?.subfolders) ? (r.subfolders as Array<Record<string, unknown>>) : []).flatMap((f) => {
            const id = s(f.id);
            return id ? [{ id, name: s(f.name) ?? "Folder", itemCount: Number(f.item_count ?? 0) || 0, thumbnailUrls: Array.isArray(f.thumbnail_urls) ? f.thumbnail_urls.filter((u): u is string => typeof u === "string") : [] }] : [];
          });
        }
        const total = Number(r?.total ?? 0);
        const perPage = Number(r?.per_page ?? PER_PAGE) || PER_PAGE;
        if (page * perPage >= total) break;
      }
      return { assets, subfolders };
    },
    async versions(assetId) {
      const r = await call<Array<Record<string, unknown>>>(`/assets/${encodeURIComponent(assetId)}/versions`);
      return (Array.isArray(r) ? r : []).flatMap((v) => {
        const id = s(v.id);
        return id ? [{ id, number: Number(v.version_number ?? 1) || 1, ready: v.processing_status === "ready", createdAt: s(v.created_at) }] : [];
      });
    },
    async stream(assetId, opts = {}) {
      const r = await call<Record<string, unknown>>(`/stream/${encodeURIComponent(assetId)}`, {
        version_id: opts.versionId,
        download: opts.download ? "true" : null,
      });
      const url = s(r?.url);
      if (!url) throw new ShareError(502, "no stream url");
      return resolveStreamUrl(url, base);
    },
    async comments(assetId, versionId) {
      return normalizeComments(await call<unknown>("/comments", { asset_id: assetId, version_id: versionId }));
    },
    async postComment(assetId, c) {
      // Anonymous on purpose: no guest_name / guest_email, the media service labels the author.
      const r = await call<unknown>("/comment", {}, {
        method: "POST",
        body: JSON.stringify({
          asset_id: assetId,
          version_id: c.versionId,
          body: c.body,
          timecode_start: c.timecode,
          parent_id: c.parentId,
          annotation: c.annotation,
          idempotency_key: c.idempotencyKey,
        }),
      });
      return normalizeComments([r])[0] ?? null;
    },
  };
}
